import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import ApiClient from '../../../ApiClient';
import { ArcRowsUpload } from '../../Common/ArcRows';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ProposedChangeSet } from '../ChangeSets/changeSets';
import { useVersionedRecord, VersionedRecordPage } from '../../Common/VersionedRecordPage';
import { formatLocale, useLocales } from '../../../utils/locales';
import BaseRatesTable from './BaseRatesTable';
import * as baseRates from './baseRates';

const BASE_RATES_PATH = '/baseRate';

export const TEMPLATE_URL = `${process.env.PUBLIC_URL || ''}/base-rates-template.xlsx`;

interface BaseRatesProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<baseRates.BaseRates>;
    // Resolves with a proposed change set instead when live models depend on the rates.
    httpPut: (path: string, body: object) => Promise<baseRates.BaseRates | ProposedChangeSet>;
  };
}

export const BaseRates = ({ apiClient }: BaseRatesProps): JSX.Element => {
  const fetchRates = useCallback(
    () => apiClient.httpGet(BASE_RATES_PATH) as Promise<baseRates.BaseRates>,
    [apiClient]
  );
  // An organization has no base rates until they are first uploaded.
  const page = useVersionedRecord(fetchRates, baseRates.describeError, true);
  const locales = useLocales(apiClient, baseRates.describeError);
  const rates = page.record;

  const create = (basePrices: baseRates.BasePrice[]): Promise<void> =>
    page.save(() => apiClient.httpPost(BASE_RATES_PATH, { basePrices }));

  const update = (basePrices: baseRates.BasePrice[]): Promise<void> =>
    page.save(() =>
      apiClient.httpPut(BASE_RATES_PATH, {
        currentLiveVersion: rates ? rates.version : null,
        basePrices,
      })
    );

  let content: JSX.Element | null = null;
  if (rates) {
    content = (
      <BaseRatesTable
        rates={rates}
        locales={locales.locales}
        onSave={update}
        onReload={page.reload}
      />
    );
  } else if (page.loadErrors.length === 0) {
    content = (
      <ArcRowsUpload
        list={baseRates.rateRows}
        locales={locales.locales}
        display={formatLocale}
        idPrefix="baseRates"
        title="Create base rates"
        description="No base rates have been set up yet. Fill in the template with one row per arc and upload it."
        templateUrl={TEMPLATE_URL}
        emptyMessage="The file has no base rates below the header row."
        ready
        onCreate={create}
        conflictHeader={baseRates.CONFLICT_HEADER}
        onReload={page.reload}
      />
    );
  }

  return (
    <VersionedRecordPage
      idPrefix="baseRates"
      page={page}
      locales={locales}
      loadingText="Loading base rates"
      loadErrorHeader="Could not load base rates"
      retryId="retryBaseRates"
    >
      {content}
    </VersionedRecordPage>
  );
};

BaseRates.propTypes = {
  apiClient: PropTypes.object.isRequired,
};

const BaseRatesView = (): JSX.Element => <BaseRates apiClient={ApiClient} />;

export default BaseRatesView;
